import { observable, action, computed } from 'mobx';

export interface FileProgress {
  data?: number[];
  downloaded: number;
  total: number;
  complete: boolean;
}

export interface FileSystemEntry {
  name: string;
  dateCreated: string;
  dateModified: string;
  isSelected: boolean;
  size?: number;
  type: 'File' | 'Folder';
  groupOrder: number;
}

//key them by file path, since you can't download two of those at once
export interface Files {
  [key: string]: FileProgress;
}

export function isLoaded(obj: any): obj is FileSystemInfo.LoadedFile {
  return 'data' in obj;
}

export class FileSystemStore {
  @observable tree: FileSystemInfo.FileTree;
  @observable pathStack: FileSystemInfo.FileTree[] = [];
  @observable
  downloads: {
    inProgress: { [key: string]: FileTransfer.Progress };
    complete: FileTransfer.Complete;
  } = {
    inProgress: {},
    complete: undefined,
  };
  @observable uploads: { [key: string]: FileTransfer.UploadProgress } = {};

  @observable goingBack: boolean = false;
  @observable isLoadingFiles: boolean = false;
  @observable searchResult: SearchResult;
  @observable searching?: boolean;

  @computed
  get fileSystemEntries(): FileSystemEntry[] {
    if (!this.tree.rootFolder.childFolders) {
      return [];
    }
    let folders = this.tree.rootFolder.childFolders.map<FileSystemEntry>(
      folder => {
        return {
          dateCreated: folder.dateCreated,
          dateModified: folder.dateModified,
          isSelected: folder.isSelected,
          name: folder.name,
          type: 'Folder',
          groupOrder: 0,
        };
      },
    );

    let files = this.tree.rootFolder.files.map<FileSystemEntry>(file => {
      return {
        dateCreated: file.dateCreated,
        dateModified: file.dateModified,
        isSelected: file.isSelected,
        name: file.path,
        type: 'File',
        size: file.fileSize,
        groupOrder: 1,
      };
    });

    return [...folders, ...files];
  }

  @action
  handleCreateRoot(drives: DriveInfo[]) {
    this.handleUpdateFileTree({
      deepWalk: false,
      rootFolderExists: true,
      rootFolder: {
        name: '',
        childFolders: drives.map(drive => {
          console.log(drive.name);
          return {
            name: drive.name,
            childFolders: [],
            files: [],
            dateCreated: '',
            dateModified: '',
          };
        }),
        files: [],
        dateCreated: '',
        dateModified: '',
      },
    });
  }

  @action
  handleSearch(result: SearchResult) {
    this.searchResult = result;
    this.searching = false;
  }

  @action
  handleClearSearch() {
    this.searchResult = undefined;
  }

  @action
  handleUploadProgress(file: FileTransfer.UploadProgress) {
    this.uploads = { ...this.uploads, [file.path]: file };
    if (file.uploaded >= file.total) {
      setTimeout(() => {
        delete this.uploads[file.path];
      }, 2000);
    }
  }

  @action
  handleRemoveDownload(path: string) {
    if (this.downloads.complete.path == path) {
      this.downloads.complete = undefined;
    }
    if (this.downloads.inProgress[path]) {
      delete this.downloads.inProgress[path];
    }
  }

  @action
  handleDownloadComplete(data: FileTransfer.Complete) {
    delete this.downloads.inProgress[data.path];
    this.downloads.complete = data;
  }

  @action
  handleDownloadProgress(data: FileTransfer.Progress) {
    /*
        let download = this.downloads[data.path]
        if (!download) {
            console.log("Got data for a file that was never started!")
            return false
        }

        if (isLoaded(data)) {
            download.data = data.data
            download.downloaded = data.total
            download.complete = true
        }
        else {
            download.downloaded = data.downloaded
        }
        for (let byte of data.fileData) {
            this.downloads[data.path].data.push(byte)
        }
        */
    //this.downloads[data.path].data.push(...data.fileData)
    this.downloads.inProgress[data.path] = data;
  }

  @action
  handleAddDownload(file: FileTransfer.Initial) {
    this.downloads.inProgress[file.path] = {
      path: file.path,
      downloaded: 0,
      total: file.size,
    };
  }

  @action
  handleReload(tree: FileSystemInfo.FileTree) {
    var currentIndex = this.currentTreeIndex();
    this.pathStack[currentIndex] = tree;
    // this.pathStack[currentIndex] != tree now because it gets converted into a mobx observable.
    this.tree = this.pathStack[currentIndex];
    console.log('refreshed', tree);
    //don't do anything to the history, right
    this.isLoadingFiles = false;
  }

  @action
  handleUpdateFileTree(tree: FileSystemInfo.FileTree) {
    console.log('Updating file tree', tree);
    if (this.currentTreeIndex() > 0) {
      this.pathStack = this.pathStack.slice(
        this.currentTreeIndex(),
        this.pathStack.length,
      );
    }

    // If we're updating the current root folder, don't
    // create a new pathstack entry.  Just update the existing.
    if (
      this.pathStack.length > 0 &&
      this.pathStack[0].rootFolder == tree.rootFolder
    ) {
      this.pathStack[0] = tree;
    } else {
      this.pathStack.unshift(tree);
    }

    this.tree = this.pathStack[0];
    if (this.pathStack.length > 10) {
      this.pathStack.pop();
    }

    this.isLoadingFiles = false;
  }

  @action
  handleBack() {
    // No navigation has occurred yet.
    if (this.pathStack.length == 1) {
      return;
    }
    this.goingBack = true;
    if (this.pathStack.indexOf(this.tree) != this.pathStack.length - 1) {
      this.tree = this.pathStack[this.currentTreeIndex() + 1];
    } else {
      this.tree = this.pathStack[1];
    }
  }

  @action
  handleForward() {
    if (this.pathStack.indexOf(this.tree) > 0) {
      this.tree = this.pathStack[this.currentTreeIndex() - 1];
    }
  }

  @action
  currentTreeIndex() {
    return this.pathStack.indexOf(this.tree);
  }

  @action.bound
  setAllSelected(areSelected: boolean) {
    if (!this.tree.rootFolder) {
      return;
    }
    if (this.tree.rootFolder.childFolders) {
      this.tree.rootFolder.childFolders.forEach(
        x => (x.isSelected = areSelected),
      );
    }
    if (this.tree.rootFolder.files) {
      this.tree.rootFolder.files.forEach(x => (x.isSelected = areSelected));
    }
  }

  @action
  setIsLoadingFiles(isLoading: boolean) {
    this.isLoadingFiles = isLoading;
  }

  @action
  setIsSearching(isSearching: boolean) {
    this.searching = isSearching;
  }

  @action
  setEntryIsSelected(
    type: 'Folder' | 'File',
    name: string,
    isSelected: boolean,
  ) {
    if (!this.tree.rootFolder) {
      return;
    }

    if (type == 'Folder') {
      if (!this.tree.rootFolder.childFolders) {
        return;
      }
      var folder = this.tree.rootFolder.childFolders.find(x => x.name == name);
      if (folder) {
        folder.isSelected = isSelected;
      }
    } else if (type == 'File') {
      if (!this.tree.rootFolder.files) {
        return;
      }
      var file = this.tree.rootFolder.files.find(x => x.path == name);
      if (file) {
        file.isSelected = isSelected;
      }
    }
  }
}

export const fileSystemStore = new FileSystemStore();
